// Fund activity sub-route — /fund/:fundId/activity (16.d.4.4).
// Fourth step of the 16.d.4 mega-page split. Consolidates the fund's
// historical ledgers + audit trail: SUBSCRIPTIONS, REDEMPTIONS, FEE PAYMENTS,
// PERFORMANCE CRYSTALLIZATIONS, and RECENT ACTIVITY (audit feed). Self-loads
// each via its existing GET endpoint using fundId from the URL, plus the fund
// (GET /ops/funds) for base_currency + name. No shared fund context yet (the
// FundLayout + subnav lands in 16.d.4.5). Manager-gated in App.tsx.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PageHeader } from '../../components/shell';
import { fmtBase, fmtUnitPriceOps } from '../../lib/format';
import { api } from '../../lib/api';

const fmtUnits = (s: string | number) =>
  Number(s).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 8 });
const fmtDT = (iso: string) => new Date(iso).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';

// "Fix Valuation" events carry the actual figures in metadata (n8 request,
// 2026-07) — surface them as a readable "$X -> $Y (reason)" line so a manager
// can see what a fix/correction actually changed without leaving this page.
function activityDetail(e: any, baseCurrency: string): string {
  const m = e.metadata || {};
  if (e.action === 'fund.valuation_fix') {
    const to = m.total_assets_base != null ? fmtBase(Number(m.total_assets_base), baseCurrency) : '?';
    const from = m.previous_total_assets_base != null
      ? fmtBase(Number(m.previous_total_assets_base), baseCurrency)
      : null;
    const change = from ? `${from} → ${to}` : `entered ${to}`;
    return m.reason ? `${change} — ${m.reason}` : change;
  }
  if (e.action === 'fund.valuation_hide') {
    return m.reason ? `hidden — ${m.reason}` : 'hidden';
  }
  return '';
}

export default function FundActivity() {
  const { fundId } = useParams<{ fundId: string }>();
  const [funds, setFunds] = useState<any[] | null>(null);
  const [subs, setSubs] = useState<any[]>([]);
  const [reds, setReds] = useState<any[]>([]);
  // This table showed PAID while naming NEITHER charge PAID is net of — the
  // same defect fixed on the API in 22.e.1. Both columns render only when a
  // row actually carries that charge, so an ordinary ledger stays five columns
  // wide rather than growing two permanent zero columns.
  const showRedemptionFee = reds.some(
    (r: any) => Number(r.redemption_fee_base ?? r.redemption_fee_usd ?? 0) !== 0);
  const showWindupFee = reds.some(
    (r: any) => Number(r.final_mgmt_fee_base ?? r.final_mgmt_fee_usd ?? 0) !== 0);
  const [payments, setPayments] = useState<any[]>([]);
  const [crystallizations, setCrystallizations] = useState<any[]>([]);
  const [auditFeed, setAuditFeed] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const fund = useMemo(() => funds?.find(f => f.fund_id === fundId) || null, [funds, fundId]);

  useEffect(() => {
    api<any[]>('/ops/funds').then(setFunds).catch(() => setErr('Failed to load fund.'));
  }, []);

  useEffect(() => {
    if (!fundId) return;
    Promise.all([
      api<any[]>(`/ops/funds/${fundId}/subscriptions`),
      api<any[]>(`/ops/funds/${fundId}/redemptions`),
      api<any[]>(`/ops/funds/${fundId}/manager-fee-payments`),
      api<any[]>(`/ops/funds/${fundId}/performance-crystallizations`),
      api<any>(`/ops/funds/${fundId}/audit?limit=20`),
    ])
      .then(([s, r, p, c, a]) => { setSubs(s); setReds(r); setPayments(p); setCrystallizations(c); setAuditFeed(a); })
      .catch(() => setErr('Failed to load fund activity.'));
  }, [fundId]);

  if (!fundId) return null;

  return (
    <>
      <PageHeader
        kicker="FUND"
        title={<>Fund <em>activity.</em></>}
        meta={fund ? <span className="label-s">{fund.name}</span> : undefined}
      />
      {err && <p className="serif-body" style={{ color: 'var(--oxblood)', marginTop: 12 }}>{err}</p>}

      {/* Subscriptions */}
      <section style={{ marginTop: 24 }}>
        <div className="kicker-row"><span className="kicker">SUBSCRIPTIONS</span></div>
        <table className="tbl" style={{ marginTop: 12 }}>
          <thead><tr><th>EFFECTIVE (UTC)</th><th>INVESTOR</th><th data-align="right">AMOUNT</th><th data-align="right">NAV / UNIT</th><th data-align="right">UNITS</th></tr></thead>
          <tbody>
            {subs.map(s => (
              <tr key={s.id}>
                <td className="num" style={{ fontSize: 12 }}>{fmtDT(s.effective_at)}</td>
                <td>{s.investor_name}</td>
                <td data-align="right" className="num">{fmtBase(Number(fund?.base_currency === 'USD' ? s.amount_usd : s.amount_base), fund?.base_currency)}</td>
                <td data-align="right" className="num">{fmtUnitPriceOps(Number(fund?.base_currency === 'USD' ? s.nav_per_unit : s.nav_per_unit_base), fund?.base_currency)}</td>
                <td data-align="right" className="num">{fmtUnits(s.units_issued)}</td>
              </tr>
            ))}
            {!subs.length && <tr><td colSpan={5} className="label-s" style={{ color: 'var(--ink-3)' }}>None.</td></tr>}
          </tbody>
        </table>
      </section>

      {reds.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <div className="kicker-row"><span className="kicker">REDEMPTIONS</span></div>
          <table className="tbl" style={{ marginTop: 12 }}>
            <thead><tr><th>EFFECTIVE (UTC)</th><th>INVESTOR</th><th data-align="right">UNITS</th><th data-align="right">NAV / UNIT</th><th data-align="right">PAID</th>{showRedemptionFee && <th data-align="right">REDEMPTION FEE</th>}{showWindupFee && <th data-align="right">MGMT FEE WITHHELD</th>}</tr></thead>
            <tbody>
              {reds.map(r => (
                <tr key={r.id}>
                  <td className="num" style={{ fontSize: 12 }}>{fmtDT(r.effective_at)}</td>
                  <td>{r.investor_name}</td>
                  <td data-align="right" className="num">{fmtUnits(r.units_redeemed)}</td>
                  <td data-align="right" className="num">{fmtUnitPriceOps(Number(fund?.base_currency === 'USD' ? r.nav_per_unit : r.nav_per_unit_base), fund?.base_currency)}</td>
                  {/* PAID before the charges, matching FundInvestorDetail, the
                      client-facing table, and the issued statement PDF. A client
                      cross-checking the portal against their statement must not
                      meet the same five figures in a different order. */}
                  <td data-align="right" className="num">{fmtBase(Number(fund?.base_currency === 'USD' ? r.amount_usd_paid : r.amount_base_paid), fund?.base_currency)}</td>
                  {showRedemptionFee && <td data-align="right" className="num">{fmtBase(Number(fund?.base_currency === 'USD' ? r.redemption_fee_usd : r.redemption_fee_base), fund?.base_currency)}</td>}
                  {showWindupFee && <td data-align="right" className="num">{fmtBase(Number(fund?.base_currency === 'USD' ? r.final_mgmt_fee_usd : r.final_mgmt_fee_base), fund?.base_currency)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {payments.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <div className="kicker-row"><span className="kicker">FEE PAYMENTS</span></div>
          <table className="tbl" style={{ marginTop: 12 }}>
            <thead><tr><th>POSTED (UTC)</th><th data-align="right">AMOUNT</th><th data-align="right">INVESTORS</th><th>POSTED BY</th><th>NOTE</th></tr></thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id}>
                  <td className="num" style={{ fontSize: 12 }}>{fmtDT(p.posted_at)}</td>
                  <td data-align="right" className="num">{fmtBase(Number(p.amount_base), fund?.base_currency)}</td>
                  <td data-align="right" className="num">{Object.keys(p.allocations || {}).length}</td>
                  <td style={{ fontSize: 12 }}>{p.posted_by_email || '—'}</td>
                  <td style={{ fontSize: 12 }}>{p.note || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {crystallizations.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <div className="kicker-row"><span className="kicker">PERFORMANCE CRYSTALLIZATIONS</span></div>
          <table className="tbl" style={{ marginTop: 12 }}>
            <thead><tr><th>PERIOD (UTC)</th><th>POSTED</th><th data-align="right">AMOUNT</th><th data-align="right">INVESTORS</th><th>SETTLED</th><th>POSTED BY</th></tr></thead>
            <tbody>
              {crystallizations.map(c => (
                <tr key={c.id}>
                  <td className="num" style={{ fontSize: 12 }}>{fmtDT(c.period_start)} → {fmtDT(c.period_end)}</td>
                  <td className="num" style={{ fontSize: 12 }}>{fmtDT(c.posted_at)}</td>
                  <td data-align="right" className="num">{fmtBase(Number(c.amount_base), fund?.base_currency)}</td>
                  <td data-align="right" className="num">{Object.keys(c.allocations || {}).length}</td>
                  <td style={{ fontSize: 12 }}>{c.settlement_tx_id ? 'yes' : '—'}</td>
                  <td style={{ fontSize: 12 }}>{c.posted_by_email || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Fund-scoped recent activity (audit feed). Always renders so the
          operator discovers the surface even on funds with no events yet. */}
      {auditFeed && (
        <section style={{ marginTop: 32 }}>
          <div className="kicker-row"><span className="kicker">RECENT ACTIVITY</span></div>
          {auditFeed.events && auditFeed.events.length > 0 ? (
            <table className="tbl" style={{ marginTop: 12 }}>
              <thead><tr><th>WHEN (UTC)</th><th>ACTION</th><th>ACTOR</th><th>TARGET</th><th>DETAIL</th></tr></thead>
              <tbody>
                {auditFeed.events.map((e: any) => (
                  <tr key={e.id}>
                    <td className="num" style={{ fontSize: 12 }}>{fmtDT(e.occurred_at)}</td>
                    <td style={{ fontSize: 12 }}>{e.action}</td>
                    <td style={{ fontSize: 12 }}>{e.actor_email || e.actor_type}</td>
                    <td style={{ fontSize: 12 }}>{e.target_type}</td>
                    <td className="num" style={{ fontSize: 12 }}>{activityDetail(e, fund?.base_currency || 'USD')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="label-s" style={{ marginTop: 12, color: 'var(--ink-3)' }}>NO RECENT ACTIVITY ON THIS FUND.</p>
          )}
        </section>
      )}
    </>
  );
}
